/**
 * progressTone.js — local audio cue that dialing is happening.
 *
 * The problem: for MULTI-LINE Power Dial (concurrency>1) the agent's
 * softphone joins a conference room BEFORE any callee picks up. Twilio
 * never emits `ringing` on the agent leg because there is nothing to ring —
 * the room is silent until a callee is bridged in. That silence is
 * indistinguishable from a broken dialer.
 *
 * This module synthesizes a classic US ringback cadence (2s on / 4s off,
 * 440+480Hz two-tone) via WebAudio, so the moment the agent clicks
 * "Start Dialing" they hear a subtle progress tone. The tone stops the
 * instant the first callee is bridged into the conference (i.e. the agent's
 * Twilio Call fires `accept`, or a `connected` event arrives from the PD
 * session poller).
 *
 * The tone is played at ~0.15 gain (subtle) so it does not compete with the
 * real callee audio.
 *
 * Contract:
 *   start()   — idempotent; safe to call repeatedly. First call creates
 *               AudioContext and begins the cadence. Subsequent calls are no-ops.
 *   restart() — stop() then start() in one call; use on Resume so the tone
 *               comes back even if it was previously playing.
 *   stop()    — idempotent; safe to call when not started. Tears down oscillators
 *               and closes the AudioContext.
 *   isPlaying() — introspection for tests / debug UI.
 *
 * Design notes:
 *   - We DO NOT reuse the Twilio Voice output device path here. The
 *     progress tone is a browser-level audio cue that MUST play even if
 *     the Twilio Device failed to register — that's precisely the failure
 *     mode we want to be audible about.
 *   - AudioContext creation MUST happen inside a user gesture (a click).
 *     The caller (PowerDialModal submit handler) is inside a click, so this
 *     is fine. If a downstream caller ever invokes start() outside a gesture,
 *     modern browsers will keep the context in 'suspended' state and no
 *     sound plays; we log a warning event in that case.
 */

const RING_ON_MS = 2000;
const RING_OFF_MS = 4000;
const TONE_FREQ_A = 440;
const TONE_FREQ_B = 480;
const GAIN = 0.15;

let ctx = null;
let gainNode = null;
let oscA = null;
let oscB = null;
let cycleTimer = null;
let playing = false;

function safeCloseCtx() {
  try {
    if (ctx && ctx.state !== 'closed') ctx.close();
  } catch (_) {}
  ctx = null;
  gainNode = null;
}

function beginTone() {
  if (!ctx || !gainNode) return;
  try {
    oscA = ctx.createOscillator();
    oscB = ctx.createOscillator();
    oscA.type = 'sine';
    oscB.type = 'sine';
    oscA.frequency.value = TONE_FREQ_A;
    oscB.frequency.value = TONE_FREQ_B;
    oscA.connect(gainNode);
    oscB.connect(gainNode);
    oscA.start();
    oscB.start();
  } catch (_) {
    // Oscillator creation can fail if ctx was closed mid-cycle — ignore.
  }
}

function endTone() {
  try { if (oscA) oscA.stop(); } catch (_) {}
  try { if (oscB) oscB.stop(); } catch (_) {}
  try { if (oscA) oscA.disconnect(); } catch (_) {}
  try { if (oscB) oscB.disconnect(); } catch (_) {}
  oscA = null;
  oscB = null;
}

function cycle() {
  if (!playing) return;
  beginTone();
  cycleTimer = setTimeout(() => {
    endTone();
    if (!playing) return;
    cycleTimer = setTimeout(() => {
      if (playing) cycle();
    }, RING_OFF_MS);
  }, RING_ON_MS);
}

export function start() {
  if (playing) return;
  try {
    const AC = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
    if (!AC) return; // No WebAudio — silently no-op (headless env / very old browser)
    ctx = new AC();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    gainNode = ctx.createGain();
    gainNode.gain.value = GAIN;
    gainNode.connect(ctx.destination);
    playing = true;
    cycle();
  } catch (_) {
    playing = false;
    safeCloseCtx();
  }
}

/**
 * restart() — force-stop then re-start the progress tone.
 * Use this on Resume (where the tone was previously stopped after a connected call)
 * so the agent hears ringing again immediately.
 */
export function restart() {
  stop();
  // Small delay so AudioContext teardown completes before creating a new one
  setTimeout(() => {
    start();
  }, 80);
}

export function stop() {
  if (!playing && !ctx) return;
  playing = false;
  if (cycleTimer) {
    clearTimeout(cycleTimer);
    cycleTimer = null;
  }
  endTone();
  safeCloseCtx();
}

export function isPlaying() {
  return playing;
}

/**
 * playSessionEndedTone — subtle descending two-note chime (C5 -> G4)
 * played when a Power Dial session ends or finishes.
 * Uses a separate AudioContext so it never interferes with the progress tone.
 */
export function playSessionEndedTone() {
  try {
    const AC = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
    if (!AC) return;
    const toneCtx = new AC();
    if (toneCtx.state === 'suspended') {
      toneCtx.resume().catch(() => {});
    }
    const now = toneCtx.currentTime;

    // Two descending notes: C5 → G4
    [
      { freq: 523.25, startAt: now,        stopAt: now + 0.25 },
      { freq: 392.00, startAt: now + 0.28, stopAt: now + 0.55 },
    ].forEach(({ freq, startAt, stopAt }) => {
      const osc = toneCtx.createOscillator();
      const gain = toneCtx.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0.0, startAt);
      gain.gain.linearRampToValueAtTime(0.20, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, stopAt);

      osc.connect(gain);
      gain.connect(toneCtx.destination);

      osc.start(startAt);
      osc.stop(stopAt + 0.05);
    });

    setTimeout(() => {
      try { toneCtx.close(); } catch (_) {}
    }, 900);
  } catch (_) {}
}

/**
 * playConnectedTone — short rising two-note chime (G4 → C5)
 * played when a lead connects so the agent has audio confirmation.
 */
export function playConnectedTone() {
  try {
    const AC = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
    if (!AC) return;
    const toneCtx = new AC();
    if (toneCtx.state === 'suspended') {
      toneCtx.resume().catch(() => {});
    }
    const now = toneCtx.currentTime;

    [
      { freq: 392.00, startAt: now,        stopAt: now + 0.15 },
      { freq: 523.25, startAt: now + 0.18, stopAt: now + 0.38 },
    ].forEach(({ freq, startAt, stopAt }) => {
      const osc = toneCtx.createOscillator();
      const gain = toneCtx.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0.0, startAt);
      gain.gain.linearRampToValueAtTime(0.18, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, stopAt);

      osc.connect(gain);
      gain.connect(toneCtx.destination);

      osc.start(startAt);
      osc.stop(stopAt + 0.05);
    });

    setTimeout(() => {
      try { toneCtx.close(); } catch (_) {}
    }, 700);
  } catch (_) {}
}
